import { useCallback, useEffect, useRef } from 'react'
import { IPoint } from '../../../grid.types'
import gridLayoutConstants from '../../../gridLayout.constants'

interface MouseDragOptions {
  ref: React.RefObject<HTMLElement>
  onStart?: (start: IPoint, event: MouseEvent) => void
  onMove: (delta: IPoint, event: MouseEvent) => void
  onEnd?: (end: IPoint, event: MouseEvent) => void
  enabled?: boolean
}

const threshold = 120
const scrollSpeed = 20

const useMouseDragHandler = ({ ref, onStart, onMove, onEnd, enabled = true }: MouseDragOptions) => {
  const startRef = useRef<IPoint>(null)
  const scrollStartRef = useRef<number>(0)
  const onMouseDown = useCallback(
    (e: MouseEvent) => {
      if (e.button !== 0) return
      const gridContainer = document.querySelector(`#${gridLayoutConstants.gridContainerId}`)
      startRef.current = { x: e.pageX, y: e.pageY }
      scrollStartRef.current = gridContainer.scrollTop
      onStart?.(startRef.current, e)

      const handleAutoScroll = (event: MouseEvent) => {
        const { clientY } = event
        const { innerHeight } = window
        const { y } = gridContainer.getBoundingClientRect()

        if (clientY > innerHeight - threshold) {
          gridContainer.scrollTop += scrollSpeed
        } else if (clientY < threshold + y) {
          gridContainer.scrollTop -= scrollSpeed
        }
      }

      const handleMouseMove = (e: MouseEvent) => {
        const current = { x: e.pageX, y: e.pageY }

        const scrollDelta = gridContainer.scrollTop - scrollStartRef.current

        const delta = {
          x: current.x - startRef.current.x,
          y: current.y - startRef.current.y + scrollDelta,
        }

        onMove(delta, e)
        handleAutoScroll(e)
      }

      const handleMouseUp = (e: MouseEvent) => {
        const end = { x: e.pageX, y: e.pageY }
        onEnd?.(end, e)
        document.removeEventListener('mousemove', handleMouseMove)
        document.removeEventListener('mouseup', handleMouseUp)
      }

      document.addEventListener('mousemove', handleMouseMove)
      document.addEventListener('mouseup', handleMouseUp)
    },
    [ref, enabled, onStart, onMove, onEnd]
  )

  useEffect(() => {
    if (!enabled || !ref.current) return
    ref.current.addEventListener('mousedown', onMouseDown)

    return () => {
      if (!ref.current) return
      ref.current.removeEventListener('mousedown', onMouseDown)
    }
  }, [ref, enabled, onStart, onMove, onEnd])
}

export default useMouseDragHandler
